import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { ApiResponse, ApiError } from '@/types/api';
import { tokenStorage } from '@/services/storage/tokenStorage';
import toast from 'react-hot-toast';

const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

// 401 на входе и при обновлении токена — неверные данные или конец сессии: обновлять токен и повторять бессмысленно
const AUTH_ENDPOINTS = ['/auth/login', '/auth/refresh'];
const isAuthEndpoint = (url?: string) => !!url && AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

let refreshInFlight: Promise<string> | null = null;

/**
 * Обновляет пару токенов — одно обновление на всех. Сервер при обновлении выдаёт новый refresh-токен
 * и отменяет старый, поэтому запросы, одновременно получившие 401, не должны слать один и тот же
 * refresh-токен: второй получал 401, и пользователя выбрасывало на вход. Пока обновление идёт,
 * все ожидающие получают его результат. Возвращает новый access-токен.
 */
export const refreshAccessToken = (): Promise<string> => {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const refreshToken = tokenStorage.getRefreshToken();
      if (!refreshToken) {
        throw new Error('Нет refresh-токена');
      }
      // Мимо интерцепторов клиента: 401 обновления не должен запускать ещё одно обновление
      const response = await axios.post(`${baseURL}/auth/refresh`, { refreshToken }, { timeout: 10000 });
      const { accessToken, refreshToken: nextRefreshToken } = response.data.data;
      tokenStorage.setTokens({ accessToken, refreshToken: nextRefreshToken });
      return accessToken as string;
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

/**
 * Сессию продолжить нельзя: refresh-токена нет или сервер его отклонил.
 * Нет связи, таймаут, 429 или сбой сервера — сессия, возможно, жива: выходить не нужно.
 */
const isSessionLost = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return true;
  const status = error.response?.status;
  return status !== undefined && status < 500 && status !== 408 && status !== 429;
};

// Создание экземпляра axios
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Интерцептор запросов для добавления токена
  client.interceptors.request.use(
    (config) => {
      const tokens = tokenStorage.getTokens();
      
      if (tokens.accessToken) {
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Интерцептор ответов для обработки ошибок
  client.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      return response;
    },
    async (requestError) => {
      let error = requestError;

      // Запрос отменён самим приложением — пользователю показывать нечего
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      const originalRequest = error.config;

      // Обработка ошибки 401 (неавторизован): обновляем токен и повторяем запрос один раз
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !isAuthEndpoint(originalRequest.url) &&
        tokenStorage.getRefreshToken()
      ) {
        originalRequest._retry = true;

        try {
          // Пока запрос шёл, токен мог уже обновить другой запрос — тогда достаточно повторить с новым
          const sentToken = String(originalRequest.headers?.Authorization ?? '').replace(/^Bearer /, '');
          const currentToken = tokenStorage.getAccessToken();
          const accessToken =
            currentToken && currentToken !== sentToken ? currentToken : await refreshAccessToken();

          // Повторный запрос с новым токеном
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return client(originalRequest);
        } catch (refreshError) {
          if (isSessionLost(refreshError)) {
            // Сессия закончилась — перенаправление на логин
            tokenStorage.clearTokens();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
          // Сервер не ответил на обновление — сессию не трогаем, показываем причину как обычную ошибку
          error = refreshError;
        }
      }

      // Ответ на запрос файла (responseType: 'blob') и при ошибке приходит Blob'ом — читаем из него JSON с причиной
      const blobData = error.response?.data;
      if (typeof Blob !== 'undefined' && blobData instanceof Blob && blobData.type.includes('json')) {
        try {
          error.response.data = JSON.parse(await blobData.text());
        } catch {
          // Не JSON — останется общий текст по статусу
        }
      }

      // Обработка других ошибок. Сервер отвечает в нескольких форматах:
      // { error: { code, message } }, { message, errors: [...] }, { message, error: 'текст' }
      const data = error.response?.data;
      // Ответ не пришёл за отведённое время: операция могла выполниться на сервере
      const isTimeout = !error.response && (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT');
      const nestedError = data?.error && typeof data.error === 'object' ? data.error : undefined;
      const validationErrors: string[] | undefined = Array.isArray(data?.errors) ? data.errors : undefined;
      const message: string =
        nestedError?.message ||
        (validationErrors?.length ? validationErrors.join('; ') : undefined) ||
        data?.message ||
        (typeof data?.error === 'string' ? data.error : undefined) ||
        (error.response
          ? `Ошибка сервера (${error.response.status})`
          : isTimeout
            ? 'Сервер не ответил вовремя. Проверьте, выполнилась ли операция, прежде чем повторять её'
            : 'Нет связи с сервером. Проверьте подключение к интернету');

      const apiError: ApiError = {
        code:
          nestedError?.code ||
          (error.response ? `HTTP_${error.response.status}` : isTimeout ? 'TIMEOUT' : 'NETWORK_ERROR'),
        message,
        details: nestedError?.details ?? validationErrors,
      };

      // Показ уведомления об ошибке (кроме некоторых случаев). Признак shown говорит обработчикам
      // (notifyError), что сообщение уже на экране и второй тост не нужен
      if (error.response?.status !== 401 && !originalRequest?.skipErrorToast) {
        // Одинаковые сообщения схлопываются в один тост: повтор запроса React Query
        // и несколько упавших запросов страницы (например, без связи) давали стопку одинаковых
        toast.error(apiError.message, { id: `api-error:${apiError.message}` });
        apiError.shown = true;
      }

      return Promise.reject({ ...error, apiError });
    }
  );

  return client;
};

// Экспорт единственного экземпляра клиента
export const apiClient = createApiClient();

/**
 * Таймаут для проведения документов: сервер может обрабатывать выпуск до 5 минут.
 * С обычными 10 секундами клиент показывал «Ошибка сети» посреди проведения,
 * и пользователь нажимал «Провести» повторно.
 */
export const POSTING_TIMEOUT_MS = 300_000;

// Типизированные методы для удобства использования
export class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = apiClient;
  }

  async get<T = any>(
    url: string, 
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  async post<T = any>(
    url: string, 
    data?: any, 
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async put<T = any>(
    url: string, 
    data?: any, 
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async patch<T = any>(
    url: string, 
    data?: any, 
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async delete<T = any>(
    url: string, 
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  // Метод для загрузки файлов
  async uploadFile<T = any>(
    url: string,
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post<ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });

    return response.data;
  }

  // Метод для скачивания файлов
  async downloadFile(url: string, filename?: string): Promise<void> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

// Экспорт экземпляра для использования
export const api = new ApiClient();
